import { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import SntActionButton from "@/cleanup/components/Buttons/SntActionButton";
import SntFavoriteIcon from "@icons/SntFavoriteIcon";
import SntTooltip from "@cleanup/components/Tooltip/SntTooltip";
import { useFavorites } from "../../contexts/FavoritesContext";

const FavoriteButton = ({
  entityType,
  entityId,
  onToggle,
  disabled = false,
  size = "sm",
  className = "",
  tooltip = true,
}) => {
  const language = useSelector((state) => state.language);
  const {
    isFavorite: checkIsFavorite,
    toggleFavorite,
    loadFavoriteIds,
    initialized,
  } = useFavorites();
  const [isLoading, setIsLoading] = useState(false);

  // Load favorite IDs for this entity type if not already loaded
  useEffect(() => {
    if (!initialized[entityType]) {
      loadFavoriteIds(entityType);
    }
  }, [entityType, initialized, loadFavoriteIds]);

  const isFavorite = checkIsFavorite(entityType, entityId);

  const handleToggle = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled || isLoading) return;

      setIsLoading(true);

      try {
        const newState = await toggleFavorite(entityType, entityId);

        // Notify parent component
        if (onToggle) {
          onToggle(newState, entityType, entityId);
        }
      } catch (error) {
        // Error handling is done in the context
        console.error("Failed to toggle favorite:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [entityType, entityId, disabled, isLoading, onToggle, toggleFavorite]
  );

  const button = (
    <SntActionButton
      onClick={handleToggle}
      disabled={disabled || isLoading}
      variant="sensolus-transparent"
      size={size}
      className={`favorite-button ${className}`}
      aria-label={
        isFavorite
          ? language.remove_from_favorites_key
          : language.add_to_favorites_key
      }
      role="button"
    >
      <SntFavoriteIcon
        size={size === "sm" ? 16 : 20}
        color={isFavorite ? "#ffc107" : "#ffffff"}
        stroke={isFavorite ? "none" : "#6c757d"}
        style={{ opacity: isLoading ? 0.5 : 1 }}
      />
    </SntActionButton>
  );

  if (tooltip) {
    return (
      <SntTooltip
        text={
          isLoading
            ? language.updating_key
            : isFavorite
            ? language.remove_from_favorites_key
            : language.add_to_favorites_key
        }
        icon={button}
      />
    );
  }

  return button;
};

FavoriteButton.propTypes = {
  entityType: PropTypes.oneOf([
    "organization",
    "device",
    "dashboard",
    "geozone",
  ]).isRequired,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onToggle: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
  tooltip: PropTypes.bool,
};

export default FavoriteButton;
